import { createContext, useContext } from 'react';

/**
 * Thème visuel des composants de réponse interactifs.
 * - 'sombre' (défaut) : apparence historique (cartes translucides sur fond foncé)
 *   → utilisée par l'exerciseur (ExerciseurPreview) qui n'enveloppe rien.
 * - 'clair' : style « carte blanche » du mockup, fourni par Quiz.jsx pendant la passation.
 *
 * Permet de basculer le DESIGN sans toucher la logique des réponses ni casser
 * les consommateurs existants (qui restent en 'sombre' par défaut).
 */
export const QuizThemeContext = createContext('sombre');

export const useQuizTheme = () => useContext(QuizThemeContext);

/**
 * Palette de classes Tailwind partagée par les composants de réponse interactifs,
 * pour basculer entre fond sombre (défaut) et carte blanche (passation quiz).
 * On ne change QUE des couleurs ; les classes structurelles (espacements, rounded,
 * border-2, scale, shadow, transition) restent dans chaque composant.
 *
 * @param {boolean} clair - true en thème clair (carte blanche), false en sombre
 */
export const answerTheme = (clair) => ({
    // Texte d'instructions
    instructions: clair ? 'text-slate-600' : 'text-slate-400',
    // Élément disponible (chip cliquable) — bouton « physique » : tranche basse + enfoncement au clic
    chip: clair
        ? 'border-slate-300 border-b-4 border-b-slate-400 bg-white text-slate-800 hover:bg-slate-50 hover:border-blue-400 active:translate-y-[2px] active:border-b-2'
        : 'border-slate-500 bg-slate-700 text-slate-200 hover:bg-slate-600 hover:border-blue-400',
    // Élément choisi / placé (bleu) — tranche basse plus foncée
    // (thème clair : micro-pop de sélection — .quiz-chip-pop, index.css, reduced-motion géré)
    selected: clair
        ? 'quiz-chip-pop border-blue-500 border-b-4 border-b-blue-600 bg-blue-50 text-blue-700 active:translate-y-[2px] active:border-b-2'
        : 'border-blue-500 bg-blue-500/20 text-blue-200',
    // Zone de dépôt / conteneur (bordure + fond)
    dropzone: clair ? 'border-slate-300 bg-slate-50' : 'border-slate-500 bg-slate-800/50',
    // Trou / slot vide
    slotEmptyBorder: clair ? 'border-slate-300' : 'border-slate-600',
    slotEmptyText: clair ? 'text-slate-400' : 'text-slate-500',
    // Survol « retirer » (rouge)
    removeHover: clair
        ? 'hover:bg-red-50 hover:border-red-400 hover:text-red-600'
        : 'hover:bg-red-500/20 hover:border-red-400 hover:text-red-200',
    // Champ de saisie
    input: clair
        ? 'text-slate-800 bg-white border-slate-300 border-b-4 border-b-slate-300 placeholder-slate-400 focus:border-blue-500'
        : 'text-slate-200 bg-slate-700/50 border-slate-600 placeholder-slate-500 focus:border-blue-500 focus:bg-slate-700',
    // Suffixe (unité…)
    suffixe: clair ? 'text-slate-600' : 'text-slate-300',
    // Élément déjà relié / estompé (Relier)
    linked: clair
        ? 'border-slate-200 bg-slate-100 text-slate-400 opacity-50'
        : 'border-slate-700 bg-slate-800/50 text-slate-600 opacity-40',

    // ── Correction sur place (2026-09-21) : trois états, le même sens partout ──
    // Un widget qui reçoit `corrige` marque ses propres éléments avec ces trois
    // classes et rien d'autre : c'est ICI que se décide la nuance, jamais dans
    // le widget, pour que les douze formats restent cohérents par construction.
    //   juste   : ce qu'il a mis, et c'est bon.
    //   faux    : ce qu'il a mis, et ce n'est pas ça.
    //   attendu : ce qu'il n'a pas mis, et qu'il fallait.
    // `attendu` est en POINTILLÉ, jamais en fond plein : même famille de couleur
    // que `juste`, donc même sens (« voilà la vérité »), mais pas la même
    // récompense, ce n'est pas lui qui l'a trouvée. Un vert plein sur les deux
    // ferait croire à un élève qui s'est trompé qu'il avait bon.
    // Sur une étiquette d'UNE lettre on ne raye pas le texte (un C rayé se lit
    // « € ») : le widget ajoute une croix à côté, cf. `marqueSigne`.
    juste: clair
        ? 'border-emerald-500 border-b-4 border-b-emerald-600 bg-emerald-50 text-emerald-800'
        : 'border-emerald-500 bg-emerald-500/20 text-emerald-200',
    faux: clair
        ? 'border-red-500 border-b-4 border-b-red-600 bg-red-50 text-red-800'
        : 'border-red-500 bg-red-500/20 text-red-200',
    attendu: clair
        ? 'border-dashed border-emerald-500 bg-white text-emerald-700'
        : 'border-dashed border-emerald-400 bg-transparent text-emerald-300',
});

/** Les trois états de correction, dans l'ordre où l'élève les lit. */
export const ETATS_CORRECTION = ['juste', 'faux', 'attendu'];

/** Le signe qui accompagne un état : une coche, une croix, rien pour l'attendu
 *  (c'est le pointillé qui le dit). Toujours À CÔTÉ du texte, jamais une
 *  rature dessus. */
export const marqueSigne = (etat) => (etat === 'juste' ? '✓' : etat === 'faux' ? '✗' : '');
